<template>
  <div class="instagram-next-auth">
    <el-dialog custom-class="with-title" :title="title" :visible.sync="visible">
      <div class="dialog-content">
        <div>キャンペーン実施アカウントを選択してください。</div>
        <div class="mt-x2" v-for="account in instagramAccounts">
          <el-radio v-model="selectedInAccount" :label="account.id" :disabled="account.existed">
            <icon
              class="mr-x2 ml-x1"
              :name="account.name"
              :picture-url="account.profile_picture_url"
              icon-type="instagram"
            ></icon
            >{{ account.name }}
            <div class="account-existed ml-x2" v-if="account.existed">登録済み</div>
          </el-radio>
        </div>
      </div>
      <div class="dialog-footer" slot="footer">
        <el-button type="default" @click="close">キャンセル</el-button>
        <el-button type="primary" @click="addInstagramAccount" :disabled="!canAddAccount"
          ><i class="mdi mdi-plus"></i>登録</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.account-existed {
  display: inline-block;
  padding: math.div($spacing-unit, 2) $spacing-unit;
  font-weight: 300;
  color: $danger-color;
  border: 1px solid $danger-color;
  border-radius: $round;
}
</style>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/basic_icon'
import api from '@/client/core/api'
import InstagramAuth from '@/client/core/instagram_auth'
import { useSettingGroupStore } from '@/client/store/useSettingGroupStore'
import { useSystemStore } from '@/client/store/useSystemStore'

export default defineComponent({
  components: {
    Icon
  },
  data() {
    return {
      visible: false,
      accountId: '',
      instagramAccounts: [],
      selectedInAccount: '',
      instagramAccessToken: ''
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup.id
    }),
    title() {
      return 'キャンペーン実施アカウントの追加'
    },
    canAddAccount() {
      if (this.selectedInAccount) {
        return true
      }

      return false
    }
  },
  methods: {
    ...mapActions(useSettingGroupStore, ['fetchAccounts']),

    /**
     * キャンペーン実施INアカウントを追加
     */
    async addInstagramAccount() {
      const account = this.instagramAccounts.find(account => account.id === this.selectedInAccount)

      if (!account) {
        return
      }

      const data = {
        id: account.id,
        groupId: this.currentGroupId,
        igId: account.ig_id,
        name: account.name,
        screenName: account.username,
        pictureUrl: account.profile_picture_url,
        accessToken: this.instagramAccessToken
      }

      const result = await api.post('/instagram_campaign_accounts', data)

      if (result.error && result.error.type === 'MAX_ACCOUNT_OVER') {
        return this.$notify({
          title: 'これ以上アカウントを追加できません。',
          message: 'グループの登録可能アカウント数を超えています。',
          customClass: 'danger',
          duration: 5000
        })
      }

      if (result.error && result.error.type === 'DUPLICATE') {
        return this.$notify({
          title: `${account.name}はすでに登録済みです。`,
          customClass: 'danger',
          duration: 5000
        })
      }

      if (result.error && result.error.type === 'FACEBOOK_PAGE_NOT_FOUND') {
        return this.$notify({
          title: 'Instagramアカウントの情報を取得できませんでした。',
          message: '管理されているFacebookページが存在しません。',
          customClass: 'danger',
          duration: 5000
        })
      }

      if (result.error || !result.data) {
        return this.$notify({
          title: 'アカウント追加処理に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      // アカウント一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)
      this.close()

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }

      this.$notify({
        title: 'アカウントを追加しました。',
        message: `${account.name}を追加しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * Instagram2回目の認証
     */
    async startInstagramAuth() {
      const data = await InstagramAuth.getAccounts()

      if (!data || !data.accounts || data.error) {
        return this.$notify({
          title: 'Facebook認証に失敗しました。',
          message: '恐れ入りますが時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      this.instagramAccessToken = data.token

      const accountIds = data.accounts.map(account => account.id)
      const existedAccounts = await api.get('/instagram_campaign_accounts/exists', {
        params: { accountId: accountIds.join(',') }
      })

      this.instagramAccounts = data.accounts
        .map(account => {
          account.existed = existedAccounts.data.indexOf(account.id) !== -1
          return account
        })
        .sort(accountA => {
          if (accountA.existed) {
            return 1
          }
          return -1
        })

      this.visible = true
    },

    /**
     * Instagramアカウントトークンの更新
     */
    async updateInstagramAccountToken() {
      const accountToken = await this.getInstagramAccountToken()

      if (!accountToken) {
        this.close()
        return
      }

      const account = accountToken.account
      if (!account) {
        this.$notify({
          title: '認証したアカウントが違うため、アクセストークンの更新に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
        this.close()
        return
      }

      const result = await api.put(`/instagram_campaign_accounts/${this.accountId}`, {
        accessToken: accountToken.token,
        name: account.name,
        screenName: account.username,
        pictureUrl: account.profile_picture_url
      })
      if (result.error || !result.data) {
        this.$notify({
          title: 'アクセストークン更新処理に失敗しました。恐れ入りますが時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        this.close()
        return
      }

      // アカウント一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)
      this.close()

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }

      this.$notify({
        title: `${account.name}のアクセストークンを更新しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * Instagramアカウントトークンを取得
     */
    async getInstagramAccountToken() {
      const data = await InstagramAuth.getAccounts()

      if (!data || !data.accounts || data.error) {
        this.$notify({
          title: 'Facebook認証に失敗しました。',
          message: '恐れ入りますが時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })

        return null
      }

      const accounts = data.accounts
      const updateAccount = accounts.find(account => account.id === this.accountId)

      return { token: data.token, account: updateAccount }
    },

    /**
     * ダイアログをあく
     */
    async open(options: { type: 'add' | 'update'; accountId: string }) {
      this.accountId = options.accountId || ''
      this.selectedInAccount = ''
      this.instagramAccessToken = ''
      this.instagramAccounts = []

      if (options.type === 'add') {
        await this.startInstagramAuth()
        return
      }

      await this.updateInstagramAccountToken()
    },

    /**
     * ダイアログを閉じる
     */
    close() {
      this.visible = false
    }
  }
})
</script>
